import React, { useState, useEffect } from 'react';
import { Card, Alert, Spinner } from 'react-bootstrap';
import useDicionario from '../../hooks/useDicionario';
import {
  DicionarioTable,
  DicionarioForm,
  DicionarioNavButtons,
  DicionarioTabs,
  ConfirmModal,
} from '../../components/dicionarios';

/**
 * Página principal de gerenciamento de dicionários
 * Interface com dois níveis de tabs: Tipos de Exame > Categorias
 */
export default function Dicionarios() {
  const [tipoExame, setTipoExame] = useState('eco');
  const [categoria, setCategoria] = useState('');

  // Hook de dicionário
  const {
    tipos,
    categorias,
    itens,
    isLoading,
    isSaving,
    error,
    selectedIndex,
    selectItem,
    formData,
    setFormData,
    isCreating,
    isEditing,
    goFirst,
    goPrevious,
    goNext,
    goLast,
    handleNew,
    handleEdit,
    handleSave,
    handleDelete,
    handleCancel,
    showDeleteConfirm,
    handleDeleteCancel,
    handleDeleteConfirm,
  } = useDicionario(tipoExame, categoria);

  // Estado para modal de confirmação ao descartar alterações
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Seleciona primeira categoria ao mudar tipo de exame
  useEffect(() => {
    if (categorias && categorias.length > 0 && !categoria) {
      setCategoria(categorias[0].id);
    }
  }, [categorias, categoria]);

  // Reset categoria ao mudar tipo
  useEffect(() => {
    setCategoria('');
  }, [tipoExame]);

  // Handler para mudança de tipo
  const handleTipoChange = novoTipo => {
    if (isEditing) {
      setPendingNavigation({ type: 'tipo', value: novoTipo });
      setShowDiscardConfirm(true);
      return;
    }
    setTipoExame(novoTipo);
    setCategoria('');
  };

  // Handler para mudança de categoria
  const handleCategoriaChange = novaCategoria => {
    if (isEditing) {
      setPendingNavigation({ type: 'categoria', value: novaCategoria });
      setShowDiscardConfirm(true);
      return;
    }
    setCategoria(novaCategoria);
  };

  // Confirma descarte de alterações e navega
  const handleDiscardConfirm = () => {
    if (pendingNavigation) {
      handleCancel(); // Cancela a edição atual
      if (pendingNavigation.type === 'tipo') {
        setTipoExame(pendingNavigation.value);
        setCategoria('');
      } else if (pendingNavigation.type === 'categoria') {
        setCategoria(pendingNavigation.value);
      }
    }
    setPendingNavigation(null);
    setShowDiscardConfirm(false);
  };

  // Cancela descarte e mantém edição
  const handleDiscardCancel = () => {
    setPendingNavigation(null);
    setShowDiscardConfirm(false);
  };

  if (error) {
    return (
      <Card style={{ marginTop: 10 }}>
        <Card.Body>
          <Alert variant="danger">
            <Alert.Heading>Erro ao carregar dados</Alert.Heading>
            <p>
              {error.message || 'Ocorreu um erro ao carregar os dicionários.'}
            </p>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  // Título da categoria atual
  const categoriaAtual = categorias?.find(c => c.id === categoria);
  const tituloCategoria = categoriaAtual?.nome;

  return (
    <Card style={{ marginTop: 10 }}>
      <Card.Header
        className="text-center"
        style={{
          backgroundColor: '#008080',
          color: 'white',
        }}
      >
        <h4 style={{ fontWeight: 'bold' }}>
          Cadastro de Dicionários
        </h4>
        <small>Gerenciamento de tabelas (código + descrição)</small>
      </Card.Header>

      <Card.Body style={{ padding: '15px' }}>
        {/* Tabs Nível 1 - Tipos de Exame */}
        <div style={{ marginBottom: 15 }}>
          <label style={{ fontWeight: 'bold' }}>Tipo de Exame</label>
          <DicionarioTabs
            items={tipos}
            activeKey={tipoExame}
            onSelect={handleTipoChange}
            size="normal"
          />
        </div>

        {/* Tabs Nível 2 - Categorias */}
        {categorias && categorias.length > 0 && (
          <div style={{ marginBottom: 15 }}>
            <label style={{ fontWeight: 'bold' }}>Categoria</label>
            <DicionarioTabs
              items={categorias}
              activeKey={categoria}
              onSelect={handleCategoriaChange}
              size="small"
              wrap={true}
            />
          </div>
        )}


        {tituloCategoria && (
          <div
            style={{
              backgroundColor: '#008080',
              color: 'white',
              padding: '10px 15px',
              borderRadius: '4px',
              marginBottom: 15,
              textAlign: 'center',
            }}
          >
            <h5 style={{ margin: 0 }}>{tituloCategoria}</h5>
          </div>
        )}

        {isLoading && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: 200 }}
          >
            <Spinner animation="border" variant="primary" />
            <span className="ml-2">Carregando...</span>
          </div>
        )}

        {!isLoading && categoria && (
          <>
            <DicionarioForm
              formData={formData}
              setFormData={setFormData}
              isCreating={isCreating}
              isEditing={isEditing}
              disabled={isSaving}
            />

            <DicionarioTable
              itens={itens}
              selectedIndex={selectedIndex}
              onSelectItem={selectItem}
              isLoading={isLoading}
              maxHeight={350}
            />

            <DicionarioNavButtons
              onFirst={goFirst}
              onPrevious={goPrevious}
              onNext={goNext}
              onLast={goLast}
              onEdit={handleEdit}
              onNew={handleNew}
              onDelete={handleDelete}
              onSave={handleSave}
              onCancel={handleCancel}
              isEditing={isEditing}
              isCreating={isCreating}
              isSaving={isSaving}
              disabled={isSaving}
              totalItems={itens?.length || 0}
              currentIndex={selectedIndex}
            />
          </>
        )}

        {!isLoading && !categoria && (
          <Alert variant="info">
            <Alert.Heading>Selecione uma categoria</Alert.Heading>
            <p>
              Escolha uma categoria nas abas acima para visualizar e gerenciar
              os itens do dicionário.
            </p>
          </Alert>
        )}
      </Card.Body>

      {/* Footer com informações */}
      <Card.Footer className="text-muted" style={{ fontSize: '0.85rem' }}>
        <div className="d-flex justify-content-between align-items-center">
          <span>
            {itens?.length || 0} registro(s) na categoria &quot;
            {tituloCategoria}&quot;
          </span>
          <span>
            {isEditing
              ? isCreating
                ? '🟡 Criando novo registro'
                : '🟡 Editando registro'
              : '🟢 Visualização'}
          </span>
        </div>
      </Card.Footer>

      {/* Modal de confirmação de exclusão */}
      <ConfirmModal
        show={showDeleteConfirm}
        onHide={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirmar Exclusão"
        message={`Deseja realmente excluir o item ${formData.codigo}?`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="danger"
        isLoading={isSaving}
      />

      {/* Modal de confirmação para descartar alterações */}
      <ConfirmModal
        show={showDiscardConfirm}
        onHide={handleDiscardCancel}
        onConfirm={handleDiscardConfirm}
        title="Descartar Alterações"
        message="Você está editando um registro. Deseja descartar as alterações?"
        confirmText="Descartar"
        cancelText="Continuar Editando"
        variant="warning"
      />
    </Card>
  );
}
